import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, Card, Col, Container, Form, Modal, Pagination, Row, Spinner } from "react-bootstrap";
import { Check, FolderHeart, Home, Mail, Phone, Search, Send, SlidersHorizontal, ThumbsDown, ThumbsUp, Users, X } from "lucide-react";
import { toast, ToastContainer } from "react-toastify";
import { useSelector } from "react-redux";
import api from "Common/api";
import { getDecodeId } from "slices/userProfile/thunk";
import { resolveColorMode } from "utils/colorMode";
import "./property-collections.css";

type Property = {
  ListingKey?: string;
  ListingId?: string;
  UnparsedAddress?: string;
  StreetNumber?: string;
  StreetName?: string;
  City?: string;
  State?: string;
  PostalCode?: string;
  PropertyType?: string;
  PropertySubType?: string;
  ListPrice?: number;
  CurrentPrice?: number;
  BedsTotal?: number;
  BedroomsTotal?: number;
  BathsTotal?: number;
  BathroomsTotalInteger?: number;
  SqFtTotal?: number;
  LivingArea?: number;
  thumbnailUrl?: string | null;
  listingKey?: string;
  listingId?: string;
  propertyType?: string;
  propertySubType?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  listPrice?: number;
  bedsTotal?: number;
  bathsTotal?: number;
  sqftTotal?: number;
  reactions?: Array<{ homeownerId: number; homeownerName: string; reaction: "like" | "dislike" }>;
};

type Homeowner = {
  homeownerId: number;
  accessType: string;
  isActive?: boolean;
  homeowner: { id: number; name: string; email: string };
};

type CollectionSummary = {
  id: number;
  name: string;
  note?: string;
  propertyCount: number;
  homeownerCount: number;
  homeownerNames?: string;
  createdAt: string;
  properties?: Property[];
  homeowners?: Array<{
    id: number;
    name: string;
    email: string;
    phone?: string;
    status: string;
    sharedAt: string;
    viewedAt?: string | null;
  }>;
};

const initialFilters = {
  search: "",
  homeTypes: "",
  minPrice: "",
  maxPrice: "",
  minBeds: "",
  minBaths: "",
  minSqft: "",
  maxSqft: "",
};

function listingKey(property: Property) {
  return String(property.ListingKey || property.listingKey || property.ListingId || property.listingId || "");
}

function propertyAddress(property: Property) {
  const street = property.UnparsedAddress || [property.StreetNumber, property.StreetName].filter(Boolean).join(" ");
  return [street, property.City || property.city, property.State || property.state, property.PostalCode || property.postalCode].filter(Boolean).join(", ") || "Address unavailable";
}

function currency(value?: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(Number(value || 0));
}

export default function PropertyCollections() {
  const [filters, setFilters] = useState(initialFilters);
  const [properties, setProperties] = useState<Property[]>([]);
  const [homeowners, setHomeowners] = useState<Homeowner[]>([]);
  const [collections, setCollections] = useState<CollectionSummary[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [selectedHomeowners, setSelectedHomeowners] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [activeView, setActiveView] = useState<"properties" | "collections">("properties");
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [page, setPage] = useState(1);
  const [totalProperties, setTotalProperties] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const propertyRequestSequence = useRef(0);
  const layoutMode = useSelector((state: any) => state.Layout.layoutModeType);

  const loadProperties = useCallback(async () => {
    const requestId = ++propertyRequestSequence.current;
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "6", page: String(page),days:"728" });
      Object.entries(filters).forEach(([key, value]) => value && params.set(key, value));
      const response: any = await api.get(`/mls-cache/properties?${params.toString()}`);
      if (requestId !== propertyRequestSequence.current) return;
      setProperties(Array.isArray(response?.properties) ? response.properties : []);
      setTotalProperties(Number(response?.total || 0));
      setTotalPages(Math.max(1, Number(response?.totalPages || 1)));
    } catch (error: any) {
      if (requestId !== propertyRequestSequence.current) return;
      toast.error(error?.response?.data?.message || "Unable to load properties");
    } finally {
      if (requestId === propertyRequestSequence.current) setLoading(false);
    }
  }, [filters, page]);

  const loadHomeowners = useCallback(async () => {
    try {
      const response: any = await api.post("/agent/accessible-homeowners", { agentId: getDecodeId() });
      setHomeowners((response?.data || []).filter((item: Homeowner) =>
        item.homeowner?.id && item.accessType === "primary" && item.isActive !== false
      ));
    } catch (_error) {
      setHomeowners([]);
    }
  }, []);

  const loadCollections = useCallback(async () => {
    try {
      const response: any = await api.get("/agent/property-collections");
      setCollections(Array.isArray(response?.data) ? response.data : []);
    } catch (_error) {
      setCollections([]);
    }
  }, []);

  useEffect(() => {
    loadHomeowners();
    loadCollections();
  }, [loadCollections, loadHomeowners]);

  useEffect(() => {
    const timer = window.setTimeout(loadProperties, 300);
    return () => window.clearTimeout(timer);
  }, [loadProperties]);

  const selectedProperties = useMemo(
    () => properties.filter((property) => selected.has(listingKey(property))),
    [properties, selected]
  );
  const visiblePropertyKeys = useMemo(
    () => properties.map(listingKey).filter(Boolean),
    [properties]
  );
  const allVisibleSelected = visiblePropertyKeys.length > 0
    && visiblePropertyKeys.every((key) => selected.has(key));

  const toggleProperty = (key: string) => {
    setSelected((current) => {
      const next = new Set(current);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const toggleAllVisibleProperties = () => {
    setSelected((current) => {
      const next = new Set(current);
      if (allVisibleSelected) {
        visiblePropertyKeys.forEach((key) => next.delete(key));
      } else {
        visiblePropertyKeys.forEach((key) => next.add(key));
      }
      return next;
    });
  };

  const toggleHomeowner = (id: number) => {
    setSelectedHomeowners((current) => {
      const next = new Set(current);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const resetShareForm = () => {
    setName("");
    setNote("");
    setSelectedHomeowners(new Set());
  };

  const shareCollection = async () => {
    if (!name.trim() || !selected.size || !selectedHomeowners.size) return;
    setSaving(true);
    try {
      const response: any = await api.post("/agent/property-collections", {
        name: name.trim(),
        note: note.trim(),
        listingKeys: Array.from(selected),
        homeownerIds: Array.from(selectedHomeowners),
        filters,
      });
      toast.success(response?.message || "Property collection shared successfully");
      setShowShare(false);
      setSelected(new Set());
      resetShareForm();
      await loadCollections();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Unable to share this collection");
    } finally {
      setSaving(false);
    }
  };

  const updateFilter = (key: keyof typeof initialFilters, value: string) => {
    setPage(1);
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const clearFilters = () => {
    setPage(1);
    setFilters(initialFilters);
  };

  const paginationItems = useMemo(() => {
    if (totalPages <= 1) return [];
    const start = Math.max(1, Math.min(page - 2, totalPages - 4));
    const end = Math.min(totalPages, start + 4);
    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  }, [page, totalPages]);

  return (
    <div className="page-content property-recommendations-page">
      <Container fluid>
        <div className="recommendations-toolbar mb-4">
          <p className="text-muted mb-0">Build a tailored property collection and share it with your homeowners.</p>
          <div className="d-flex gap-2">
            <Button variant={activeView === "properties" ? "primary" : "light"} onClick={() => setActiveView("properties")}>
              Browse properties
            </Button>
            <Button variant={activeView === "collections" ? "primary" : "light"} onClick={() => setActiveView("collections")}>
              Shared collections <Badge bg="secondary" className="ms-1">{collections.length}</Badge>
            </Button>
          </div>
        </div>

        {activeView === "properties" ? (
          <>
            <Card className="filter-card mb-4">
              <Card.Body>
                <div className="d-flex align-items-center gap-2 mb-3 fw-semibold"><SlidersHorizontal size={18} /> Find the right properties</div>
                <Row className="g-3">
                  <Col xl={3} md={6}>
                    <Form.Label>Location, address, ZIP or MLS #</Form.Label>
                    <div className="app-search-field"><Search className="app-field-icon" aria-hidden="true" /><Form.Control value={filters.search} onChange={(e) => updateFilter("search", e.target.value)} placeholder="Search listings" /></div>
                  </Col>
                  <Col xl={2} md={6}>
                    <Form.Label>Property type</Form.Label>
                    <Form.Select value={filters.homeTypes} onChange={(e) => updateFilter("homeTypes", e.target.value)}>
                      <option value="">All types</option><option value="houses">House</option><option value="condos">Condo</option><option value="multi_family">Multi-family</option><option value="lots">Land / lot</option><option value="rent">Rental</option><option value="Commercial Lease">Commercial lease</option>
                    </Form.Select>
                  </Col>
                  <Col xl={2} md={3} xs={6}><Form.Label>Min price</Form.Label><Form.Control type="number" min="0" value={filters.minPrice} onChange={(e) => updateFilter("minPrice", e.target.value)} placeholder="$ Min" /></Col>
                  <Col xl={2} md={3} xs={6}><Form.Label>Max price</Form.Label><Form.Control type="number" min="0" value={filters.maxPrice} onChange={(e) => updateFilter("maxPrice", e.target.value)} placeholder="$ Max" /></Col>
                  <Col xl={1} md={2} xs={4}><Form.Label>Beds</Form.Label><Form.Select value={filters.minBeds} onChange={(e) => updateFilter("minBeds", e.target.value)}><option value="">Any</option>{[1,2,3,4,5].map((n) => <option key={n} value={n}>{n}+</option>)}</Form.Select></Col>
                  <Col xl={1} md={2} xs={4}><Form.Label>Baths</Form.Label><Form.Select value={filters.minBaths} onChange={(e) => updateFilter("minBaths", e.target.value)}><option value="">Any</option>{[1,2,3,4,5].map((n) => <option key={n} value={n}>{n}+</option>)}</Form.Select></Col>
                  <Col xl={1} md={2} xs={4}><Form.Label>Min sqft</Form.Label><Form.Control type="number" min="0" value={filters.minSqft} onChange={(e) => updateFilter("minSqft", e.target.value)} placeholder="Any" /></Col>
                </Row>
                {Object.values(filters).some(Boolean) && <Button variant="link" className="px-0 mt-2 text-decoration-none" onClick={clearFilters}><X size={15} /> Clear filters</Button>}
              </Card.Body>
            </Card>

            {loading ? (
              <div className="text-center py-5" role="status" aria-live="polite"><Spinner animation="border" aria-hidden="true" /><div className="text-muted mt-2">Loading listings…</div></div>
            ) : properties.length === 0 ? (
              <Card><Card.Body className="text-center py-5"><Home size={42} className="text-muted mb-3" /><h5>No matching properties</h5><p className="text-muted mb-0">Try widening the price range or changing the property type.</p></Card.Body></Card>
            ) : (
              <>
                <div className="property-bulk-actions">
                  <span><strong>{properties.length}</strong> of <strong>{totalProperties}</strong> matching {totalProperties === 1 ? "property" : "properties"} shown</span>
                  <Button type="button" size="sm" variant={allVisibleSelected ? "outline-secondary" : "outline-primary"} onClick={toggleAllVisibleProperties}>
                    {allVisibleSelected ? <><X size={16} /> Deselect all shown</> : <><Check size={16} /> Select all shown</>}
                  </Button>
                </div>
                <Row className="g-4 pb-5">
                  {properties.map((property) => {
                    const key = listingKey(property);
                    const isSelected = selected.has(key);
                    const beds = property.BedsTotal ?? property.BedroomsTotal;
                    const baths = property.BathsTotal ?? property.BathroomsTotalInteger;
                    const sqft = property.SqFtTotal ?? property.LivingArea;
                    return (
                      <Col xxl={3} xl={4} md={6} key={key}>
                        <Card
                          className={`property-select-card h-100 ${isSelected ? "selected" : ""}`}
                          onClick={() => toggleProperty(key)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              toggleProperty(key);
                            }
                          }}
                          role="button"
                          tabIndex={0}
                          aria-pressed={isSelected}
                        >
                          <div className="property-photo-wrap">
                            {property.thumbnailUrl ? <img src={property.thumbnailUrl} alt={propertyAddress(property)} /> : <div className="property-photo-placeholder"><Home size={38} /></div>}
                            <span className={`property-check ${isSelected ? "active" : ""}`}>{isSelected && <Check size={17} />}</span>
                            <Badge className="property-type">{property.PropertySubType || property.PropertyType || "Property"}</Badge>
                          </div>
                          <Card.Body>
                            <h5 className="mb-2">{currency(property.CurrentPrice || property.ListPrice)}</h5>
                            <p className="text-muted small property-address">{propertyAddress(property)}</p>
                            <div className="property-facts"><span>{beds || "—"} beds</span><span>{baths || "—"} baths</span><span>{sqft ? Number(sqft).toLocaleString() : "—"} sqft</span></div>
                            <small className="text-muted d-block mt-3">MLS #{property.ListingId || key}</small>
                          </Card.Body>
                        </Card>
                      </Col>
                    );
                  })}
                </Row>
                {totalPages > 1 && (
                  <div className="property-pagination-wrap">
                    <div className="text-muted small">Page {page} of {totalPages}</div>
                    <Pagination className="mb-0">
                      <Pagination.First disabled={page === 1} onClick={() => setPage(1)} />
                      <Pagination.Prev disabled={page === 1} onClick={() => setPage((current) => Math.max(1, current - 1))} />
                      {paginationItems.map((pageNumber) => <Pagination.Item key={pageNumber} active={pageNumber === page} onClick={() => setPage(pageNumber)}>{pageNumber}</Pagination.Item>)}
                      <Pagination.Next disabled={page === totalPages} onClick={() => setPage((current) => Math.min(totalPages, current + 1))} />
                      <Pagination.Last disabled={page === totalPages} onClick={() => setPage(totalPages)} />
                    </Pagination>
                  </div>
                )}
              </>
            )}

            {selected.size > 0 && (
              <div className="selection-bar">
                <div><strong>{selected.size} {selected.size === 1 ? "property" : "properties"} selected</strong><span className="text-muted ms-2 d-none d-sm-inline">Ready to create a collection</span></div>
                <div className="d-flex gap-2"><Button variant="light" onClick={() => setSelected(new Set())}>Clear</Button><Button variant="primary" onClick={() => setShowShare(true)}><Send size={17} className="me-1" /> Create & share</Button></div>
              </div>
            )}
          </>
        ) : (
          collections.length ? <Row className="g-4">{collections.map((collection) => (
            <Col xl={6} key={collection.id}>
              <Card className="h-100 collection-card">
                <Card.Body>
                  <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
                    <div className="d-flex align-items-center gap-3"><div className="collection-icon"><FolderHeart size={22} /></div><div><h5 className="mb-1">{collection.name}</h5><div className="text-muted small">Shared {new Date(collection.createdAt).toLocaleDateString()}</div></div></div>
                    <div className="d-flex gap-2"><Badge bg="light" text="dark"><Home size={14} /> {collection.propertyCount}</Badge><Badge bg="light" text="dark"><Users size={14} /> {collection.homeownerCount}</Badge></div>
                  </div>
                  {collection.note && <p className="collection-note">{collection.note}</p>}

                  <div className="collection-detail-section">
                    <h6>Shared properties</h6>
                    <div className="shared-property-list">
                      {(collection.properties || []).map((property) => {
                        const price = property.CurrentPrice || property.ListPrice || property.listPrice;
                        const beds = property.BedsTotal ?? property.BedroomsTotal ?? property.bedsTotal;
                        const baths = property.BathsTotal ?? property.BathroomsTotalInteger ?? property.bathsTotal;
                        const sqft = property.SqFtTotal ?? property.LivingArea ?? property.sqftTotal;
                        return <div className="shared-property-item" key={listingKey(property)}>
                          <div className="shared-property-thumb">{property.thumbnailUrl ? <img src={property.thumbnailUrl} alt="" /> : <Home size={22} />}</div>
                          <div className="min-w-0 flex-grow-1"><strong>{currency(price)}</strong><div className="text-muted small text-truncate">{propertyAddress(property)}</div><div className="shared-property-facts"><span>{beds || "—"} beds</span><span>{baths || "—"} baths</span><span>{sqft ? Number(sqft).toLocaleString() : "—"} sqft</span><span>MLS #{property.ListingId || property.listingId || listingKey(property)}</span></div>{Boolean(property.reactions?.length) && <div className="agent-property-feedback">{property.reactions?.map((reaction) => <span key={reaction.homeownerId} className={reaction.reaction}><>{reaction.reaction === "like" ? <ThumbsUp size={12} /> : <ThumbsDown size={12} />}</> {reaction.homeownerName || "Homeowner"}</span>)}</div>}</div>
                        </div>;
                      })}
                    </div>
                  </div>

                  <div className="collection-detail-section mt-3">
                    <h6>Shared with</h6>
                    <div className="shared-homeowner-list">
                      {(collection.homeowners || []).map((homeowner) => <div className="shared-homeowner-item" key={homeowner.id}>
                        <span className="homeowner-avatar">{(homeowner.name || homeowner.email || "H").charAt(0).toUpperCase()}</span>
                        <div className="min-w-0 flex-grow-1"><strong className="d-block">{homeowner.name || "Homeowner"}</strong><div className="text-muted small text-truncate"><Mail size={13} /> {homeowner.email}</div>{homeowner.phone && <div className="text-muted small"><Phone size={13} /> {homeowner.phone}</div>}</div>
                        <Badge bg={homeowner.status === "viewed" ? "success" : "primary"}>{homeowner.status === "viewed" ? "Viewed" : "Shared"}</Badge>
                      </div>)}
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}</Row> : <Card><Card.Body className="text-center py-5"><FolderHeart size={42} className="text-muted mb-3" /><h5>No shared collections yet</h5><p className="text-muted">Select properties to create your first recommendation collection.</p><Button onClick={() => setActiveView("properties")}>Browse properties</Button></Card.Body></Card>
        )}
      </Container>

      <Modal show={showShare} onHide={() => !saving && setShowShare(false)} size="lg" centered>
        <Modal.Header closeButton><Modal.Title>Create and share collection</Modal.Title></Modal.Header>
        <Modal.Body>
          <Row className="g-4">
            <Col md={6}>
              <Form.Group className="mb-3"><Form.Label>Collection name</Form.Label><Form.Control autoFocus maxLength={160} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Downtown homes under $500k" /></Form.Group>
              <Form.Group><Form.Label>Message (optional)</Form.Label><Form.Control as="textarea" rows={4} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Tell the homeowner why you selected these properties." /></Form.Group>
              <div className="selected-property-summary mt-3"><Home size={18} /><div><strong>{selected.size} properties selected</strong><div className="small text-muted">{selectedProperties.slice(0, 2).map(propertyAddress).join(" • ")}{selected.size > 2 ? ` • +${selected.size - 2} more` : ""}</div></div></div>
            </Col>
            <Col md={6}>
              <Form.Label>Share with homeowners</Form.Label>
              <div className="homeowner-picker">
                {homeowners.length ? homeowners.map((item) => {
                  const id = Number(item.homeowner.id);
                  const checked = selectedHomeowners.has(id);
                  return <button type="button" key={id} className={`homeowner-option ${checked ? "selected" : ""}`} onClick={() => toggleHomeowner(id)}><span className="homeowner-avatar">{(item.homeowner.name || item.homeowner.email || "H").charAt(0).toUpperCase()}</span><span className="text-start flex-grow-1"><strong className="d-block">{item.homeowner.name || "Homeowner"}</strong><small className="text-muted">{item.homeowner.email} · {item.accessType}</small></span><span className="option-check">{checked && <Check size={15} />}</span></button>;
                }) : <div className="text-center text-muted p-4"><Users size={30} className="mb-2" /><div>No connected homeowners found.</div><small>Add or invite a homeowner before sharing.</small></div>}
              </div>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer><Button variant="light" onClick={() => setShowShare(false)} disabled={saving}>Cancel</Button><Button onClick={shareCollection} disabled={saving || !name.trim() || !selectedHomeowners.size}>{saving ? <><Spinner size="sm" className="me-2" />Sharing…</> : <><Send size={16} className="me-1" /> Share collection</>}</Button></Modal.Footer>
      </Modal>
      <ToastContainer position="top-right" theme={resolveColorMode(layoutMode)} />
    </div>
  );
}
